import React, {useCallback, useState, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import Highlighter from 'react-highlight-words';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from 'common/constants';
import {debounce} from 'lodash';

import {AutoSuggest, IconButton, Layout, ListItem, Text} from '@phonectas/unifon-ui-kit';
import {isValidPhone} from 'utils/phoneNumber';
import {serviceStatusesColor} from 'utils/div';
import {DATE_FORMATS, dateByCustomFormat} from 'utils/date';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {Accounts} from 'common/types/store/threeSixtyTypes';
import Search from '@phonectas/unifon-ui-kit/dist/icons/Search';

interface ThreeSixtySearchBarProps {
	closeSearch?: boolean;
}

const ThreeSixtySearchBar: React.FC<ThreeSixtySearchBarProps> = ({closeSearch}) => {
	const [t] = useTranslation();
	const navigate = useNavigate();
	const {
		getAccountList,
		getUser,
	} = useThreeSixtyNewStore((state) => ({
		getAccountList: state.getAccountList,
		getUser: state.getUser,
	}), shallow);
	const [value, setValue] = useState('');
	const accountsList = useThreeSixtyNewStore((state) => state.accounts);
	const accounts = accountsList?.accounts;
	const debouncedFetchList = useMemo(() => debounce((searchValue) => {
		getAccountList(10, searchValue, 'serviceStatusAsc');
	}, 500), [getAccountList]);

	const handleChange = useCallback((
		value: string | number | string[] | number[] | null | undefined,
		_e: React.SyntheticEvent,
		_name: string
	) => {
		if (value === null || value === undefined) {
			return;
		}
		if (typeof value === 'string') {
			setValue(value);
			debouncedFetchList(value);
		}

	}, [debouncedFetchList]);

	const onClick = useCallback((item: string) => {
		if (isValidPhone(value)) {
			getUser({
				phone: value,
				accountId: item,
				onSuccess: () => {
					navigate(`${ROUTES.THREE_SIXTY}/${item}`);
				}
			});
		} else {
			navigate(`${ROUTES.THREE_SIXTY}/${item}`);
		}
	}, [navigate, getUser, value]);

	const renderOption = useCallback(
		({
			index,
			style,
			data: {collection},
		}: {
			index: number;
			style: React.CSSProperties;
			data: {collection: Accounts[]};
		}) => {
			if (!collection || !collection[index]) {
				return <div key={index}>No data</div>
			}
			const item = collection[index];
			const userPhone = item?.user_details?.phone_number;
			const userName = item?.user_details?.user_name;
			const isUser = item?.user_details?.is_user;
			const companyName = item?.name;
			const endDate = item?.user_details?.end_date;
			const status = item?.user_details?.service_status?.toString() || '';

			return (
				<ListItem
					key={index}
					value={item.id}
					onClick={() => onClick(item.id)}
					style={style}
					primaryText={
						<>
							<Text variant="h5">
								<Highlighter
									autoEscape
									searchWords={[value]}
									textToHighlight={isUser ? userPhone : companyName ? companyName : ''}
								/>
							</Text>
							{
								endDate && (
									<Text>
										{dateByCustomFormat(endDate, DATE_FORMATS.DD_MM_YY)}
									</Text>
								)
							}
						</>
					}
					secondaryText={
						<>
							{isUser ? (
								<Layout fullWidth justifyContent="space-between" nowrap>
									<Text color="textSecondary" variant="caption">
										{item.name && userName ? `${userName} - (${item.name})` : item.name}
									</Text>
									<Text
										style={{
											color: serviceStatusesColor(status).priColor,
										}}
										variant="caption"
									>
										{serviceStatusesColor(status).text}
									</Text>
								</Layout>
							) : (
								<Text color="textSecondary" variant="caption">
									{`${t('VAT')}: ${item.vat_number}`}
								</Text>
							)}
						</>
					}
				/>
			);
		},
		[value, onClick, t]
	);

	// Load a fresh list each time the search opens (closeSearch is true while it is shown)
	useEffect(() => {
		if (closeSearch) {
			setValue('');
			getAccountList();
		}
	}, [closeSearch, getAccountList]);

	// The search unmounts when closed, so a search typed just before must not run afterwards
	useEffect(() => () => debouncedFetchList.cancel(), [debouncedFetchList]);

	return (
		<Layout nowrap fullWidth>
			<AutoSuggest
				placeholder={t('Search for customers')}
				fullWidth
				compact
				itemSize={55}
				onChange={handleChange}
				disableFiltering
				collection={accounts || []}
				renderItem={renderOption}
				endAdornment={
					<IconButton size="xs" disabled>
						<Search/> 
					</IconButton>
				}
			/>
		</Layout>
	);
}

export default React.memo(ThreeSixtySearchBar);
